import React from "react";
import { useFormState } from "react-final-form";
import { ConsentLinkButton, ConsentSettingRow } from "../../../consentBanner/ConsentSettingRow";
import { ConsentColorField } from "../../../consentBanner/ConsentColorField";

export const ICON_PRIMARY_COLOR_DEFAULT = "#123456";
export const ICON_SECONDARY_COLOR_DEFAULT = "#E8E5EE";

interface ConsentBannerIconColorRowProps {
  usBannerEnabled?: boolean;
  canadaBannerEnabled?: boolean;
  eeaukBannerEnabled?: boolean;
  formApi: {
    change: (field: string, value: string) => void;
  };
}

const IconPreview = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <figure className="m-0 flex items-center gap-[8px] text-[12px] leading-[16px] text-neutral-600">
    {children}
    <figcaption>{label}</figcaption>
  </figure>
);

export const ConsentBannerIconColorRow = ({
  formApi,
  usBannerEnabled,
  canadaBannerEnabled,
  eeaukBannerEnabled,
}: ConsentBannerIconColorRowProps) => {
  const { values } = useFormState<{
    bannerIconColorPrimary?: string;
    bannerIconColorSecondary?: string;
  }>({ subscription: { values: true } });
  const primary = values.bannerIconColorPrimary || ICON_PRIMARY_COLOR_DEFAULT;
  const secondary = values.bannerIconColorSecondary || ICON_SECONDARY_COLOR_DEFAULT;
  const isDefault =
    primary === ICON_PRIMARY_COLOR_DEFAULT && secondary === ICON_SECONDARY_COLOR_DEFAULT;

  return (
    <ConsentSettingRow
      title="Icon colors"
      helpText="The icon visitors click to reopen their consent preferences."
    >
      <div className="flex flex-wrap gap-[12px]">
        <ConsentColorField field="bannerIconColorPrimary" label="Primary" />
        <ConsentColorField field="bannerIconColorSecondary" label="Secondary" />
      </div>
      {(usBannerEnabled || canadaBannerEnabled || eeaukBannerEnabled) && (
        <div className="flex flex-wrap items-center gap-[20px] rounded-lg border border-dashed border-neutral-300 bg-neutral-0 px-[14px] py-[10px]">
          {(canadaBannerEnabled || eeaukBannerEnabled) && (
            <IconPreview
              label={[
                eeaukBannerEnabled ? "EEA/UK/CH" : null,
                canadaBannerEnabled ? "Canada" : null,
              ]
                .filter(Boolean)
                .join(" and ")}
            >
              <svg viewBox="-2 -2 28 28" width="30" height="30" aria-hidden="true">
                <circle r="14" cx="12" cy="12" fill={secondary} />
                <path
                  fill={primary}
                  d="M21.95 10.99c-1.79-.03-3.7-1.95-2.68-4.22-2.98 1-5.77-1.59-5.19-4.56C6.95.71 2 6.58 2 12c0 5.52 4.48 10 10 10 5.89 0 10.54-5.08 9.95-11.01M8.5 15c-.83 0-1.5-.67-1.5-1.5S7.67 12 8.5 12s1.5.67 1.5 1.5S9.33 15 8.5 15m2-5C9.67 10 9 9.33 9 8.5S9.67 7 10.5 7s1.5.67 1.5 1.5-.67 1.5-1.5 1.5m4.5 6c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1"
                />
              </svg>
            </IconPreview>
          )}
          {usBannerEnabled && (
            <IconPreview label="US">
              <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true">
                <circle r="12" cx="12" cy="12" fill={secondary} />
                <path
                  fillRule="evenodd"
                  fill={primary}
                  d="m6.25 11.5 0 0 -2.4 -2.4 1 -1 2.35 2.35 4.4 -4.4 1 1 -5.4 5.4z M 3.5 20.5 A 12 12 0 0 1 20.5 3.5 z"
                />
                <path
                  fillRule="evenodd"
                  fill={primary}
                  d="m16 15 2 -2 1 1 -2 2 2 2 -1 1 -2 -2 -2 2 -1 -1 2 -2 -2 -2 1 -1z"
                />
              </svg>
            </IconPreview>
          )}
        </div>
      )}
      {!isDefault && (
        <ConsentLinkButton
          onClick={() => {
            formApi.change("bannerIconColorPrimary", ICON_PRIMARY_COLOR_DEFAULT);
            formApi.change("bannerIconColorSecondary", ICON_SECONDARY_COLOR_DEFAULT);
          }}
        >
          Reset to default colors
        </ConsentLinkButton>
      )}
    </ConsentSettingRow>
  );
};
